/**
 * How live flight status reads on the Trips list and the trip page. The
 * server (server/flight-status.js, server/flight-insights.js) sends times as
 * UTC instants with each airport's time zone; everything here formats them in
 * the airport's own clock, the way airlines and boarding passes do.
 */

/** Status opens this long before departure (server LIVE_BEFORE_DEPARTURE_MS). */
export const LIVE_BEFORE_DEPARTURE_HOURS = 46;

const STATES = {
  on_time: { label: 'On time', tone: 'good' },
  delayed: { label: 'Delayed', tone: 'warn' },
  departed: { label: 'Departed', tone: 'info' },
  landed: { label: 'Landed', tone: 'good' },
  arrived: { label: 'Arrived', tone: 'good' },
  cancelled: { label: 'Cancelled', tone: 'bad' },
  diverted: { label: 'Diverted', tone: 'warn' },
  unknown: { label: 'Status unavailable', tone: 'muted' },
};

/** Tailwind classes per tone, for pills and dots. */
export const STATUS_TONE_CLASSES = {
  good: { pill: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200', dot: 'bg-emerald-400' },
  warn: { pill: 'border-amber-400/30 bg-amber-400/10 text-amber-200', dot: 'bg-amber-400' },
  bad: { pill: 'border-rose-400/30 bg-rose-400/10 text-rose-200', dot: 'bg-rose-400' },
  info: { pill: 'border-sky-400/30 bg-sky-400/10 text-sky-200', dot: 'bg-sky-400' },
  muted: { pill: 'border-white/15 bg-white/5 text-white/60', dot: 'bg-white/40' },
};

/** 45 → "45 min", 80 → "1 hr 20 min". */
export function formatDelay(minutes) {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return m % 60 ? `${h} hr ${m % 60} min` : `${h} hr`;
}

/** "7:09 PM" in the airport's zone; null when either is missing. */
export function clockAt(iso, timeZone) {
  const ms = Date.parse(iso || '');
  if (!Number.isFinite(ms) || !timeZone) return null;
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit' }).format(new Date(ms));
  } catch {
    return null;
  }
}

/** "Tue, Dec 15" in the given zone (the browser's when none). */
export function dayAt(iso, timeZone) {
  const ms = Date.parse(iso || '');
  if (!Number.isFinite(ms)) return null;
  try {
    return new Intl.DateTimeFormat('en-US', { ...(timeZone ? { timeZone } : {}), weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(ms));
  } catch {
    return null;
  }
}

/** The headline for a status: { label, tone }, with the delay in the label. */
export function statusHeadline(status) {
  if (!status) return null;
  const base = STATES[status.state] || STATES.unknown;
  if (status.state === 'delayed' && Number(status.departure_delay_minutes) > 0) {
    return { ...base, label: `Delayed ${formatDelay(status.departure_delay_minutes)}` };
  }
  if (status.state === 'departed') {
    // Taxiing out until it leaves the runway, then in the air.
    const phase = status.details?.actual_off ? 'In the air' : 'Taxiing out';
    return Number(status.arrival_delay_minutes) >= 15
      ? { label: `${phase} · ${formatDelay(status.arrival_delay_minutes)} late`, tone: 'warn' }
      : { label: phase, tone: 'info' };
  }
  return base;
}

/** "Boeing 737-900 · 2,403 mi · 5h 28m", from today's flight record. */
export function flightFactsLine(details) {
  if (!details) return null;
  const parts = [];
  const plane = aircraftName(details.aircraft_type);
  if (plane) parts.push(plane);
  if (details.distance_miles) parts.push(`${Number(details.distance_miles).toLocaleString('en-US')} mi`);
  if (details.duration_minutes) {
    const h = Math.floor(details.duration_minutes / 60);
    const m = details.duration_minutes % 60;
    parts.push(h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`);
  }
  return parts.length ? parts.join(' · ') : null;
}

/** "Also sold as AA 6123 and QR 3891": the same plane under other flight numbers. */
export function codesharesLine(details) {
  const list = details?.codeshares || [];
  if (!list.length) return null;
  const joined = list.length === 1 ? list[0] : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
  return `Also sold as ${joined}`;
}

/** "64°F · Partly cloudy · Light winds". */
export function weatherLine(w) {
  if (!w) return null;
  const parts = [w.temp_f != null ? `${w.temp_f}°F` : null, w.summary, w.wind].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

/** "Cruising at 35,000 ft · 520 mph", "Climbing through 12,000 ft · 380 mph". */
export function positionLine(p) {
  if (!p || !Number.isFinite(p.altitude_ft) || p.altitude_ft <= 0) return null;
  const feet = `${Math.round(p.altitude_ft / 100) * 100}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const verb = p.altitude_change === 'C' ? 'Climbing through' : p.altitude_change === 'D' ? 'Descending through' : 'Cruising at';
  return [`${verb} ${feet} ft`, p.groundspeed_mph ? `${p.groundspeed_mph} mph` : null].filter(Boolean).join(' · ');
}

/**
 * One end of the flight as the page shows it: the time to trust (actual,
 * else estimated, else scheduled), the scheduled time when it differs, and
 * the gate and terminal.
 */
export function flightEnd(status, end) {
  if (!status) return null;
  const tz = end === 'departure' ? status.origin_timezone : status.destination_timezone;
  const scheduled = end === 'departure' ? status.scheduled_out : status.scheduled_in;
  const estimated = end === 'departure' ? status.estimated_out : status.estimated_in;
  const actual = end === 'departure' ? status.actual_out : (status.actual_in || status.actual_on);
  const best = actual || estimated || scheduled;
  const time = clockAt(best, tz);
  const scheduledTime = clockAt(scheduled, tz);
  const moved = Boolean(scheduled && best && Math.abs(Date.parse(best) - Date.parse(scheduled)) >= 5 * 60000);
  return {
    time,
    scheduledTime: moved ? scheduledTime : null,
    verb: end === 'departure'
      ? (status.actual_out ? 'Departed' : 'Departs')
      : (status.actual_in ? 'Arrived' : status.actual_on ? 'Landed' : 'Arrives'),
    terminal: (end === 'departure' ? status.terminal_origin : status.terminal_destination) || null,
    gate: (end === 'departure' ? status.gate_origin : status.gate_destination) || null,
    baggage: end === 'arrival' ? status.baggage_claim || null : null,
  };
}

/** "Terminal S · Gate B12", or null. */
export function gateLine(end) {
  if (!end) return null;
  const parts = [end.terminal && `Terminal ${end.terminal}`, end.gate && `Gate ${end.gate}`].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * What a Trips card shows after its reference number: the time that matters
 * now ({ end, verb, time } reads "Departs 6:25 AM", "Arrives 6:58 PM",
 * "Arrived 9:19 PM") and one extra (the departure gate, or the bags carousel
 * after landing). Null when there is no time to show.
 */
export function cardStatusText(status) {
  if (!status || status.state === 'cancelled') return null;
  let line;
  if (['landed', 'arrived'].includes(status.state)) {
    const arr = flightEnd(status, 'arrival');
    line = { end: 'arrival', verb: arr.verb, time: arr.time, extra: arr.baggage ? `Bags: carousel ${arr.baggage}` : null };
  } else if (status.state === 'departed' || status.state === 'diverted') {
    const arr = flightEnd(status, 'arrival');
    line = { end: 'arrival', verb: 'Arrives', time: arr.time, extra: null };
  } else {
    const dep = flightEnd(status, 'departure');
    line = { end: 'departure', verb: dep.verb, time: dep.time, extra: gateLine(dep) };
  }
  return line.time ? line : null;
}

/** "Updated just now" / "Updated 4 min ago" / "Updated 2 hr ago". */
export function updatedAgo(iso, nowMs = Date.now()) {
  const ms = Date.parse(iso || '');
  if (!Number.isFinite(ms)) return null;
  const min = Math.max(0, Math.round((nowMs - ms) / 60000));
  if (min < 1) return 'Updated just now';
  if (min < 60) return `Updated ${min} min ago`;
  return `Updated ${Math.round(min / 60)} hr ago`;
}

/** "2h 05m", "45m": the time left to a live time today; null once it's time or a day away. */
export function countdownTo(iso, nowMs = Date.now()) {
  const ms = Date.parse(iso || '');
  if (!Number.isFinite(ms)) return null;
  const min = Math.round((ms - nowMs) / 60000);
  if (min <= 0) return null;
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  return h < 24 ? `${h}h ${String(min % 60).padStart(2, '0')}m` : null;
}

/**
 * The home card's countdown, from the time to trust: { verb, time } to the
 * airline's departure on the ground ("Leaves in 2h 05m"), or to landing in
 * the air. Null once there's nothing left to count down to.
 */
export function liveCountdown(status, nowMs = Date.now()) {
  if (!status) return null;
  if (status.state === 'departed') {
    const time = countdownTo(status.estimated_in || status.scheduled_in, nowMs);
    return time ? { verb: 'Lands in', time } : null;
  }
  if (!['on_time', 'delayed'].includes(status.state) || status.actual_out) return null;
  const time = countdownTo(status.estimated_out || status.scheduled_out, nowMs);
  return time ? { verb: 'Leaves in', time } : null;
}

/**
 * "Likely to leave late" (server/flight-foresight.js): the plane for the
 * flight is coming in too late for the airline's time to hold. The same
 * facts as the email: { label, text }, or null. `cityFor` turns an airport
 * code into a city.
 */
export function forecastLine(forecast, status, cityFor = (c) => c) {
  if (!forecast?.likely_out) return null;
  const tz = status?.origin_timezone;
  const likely = clockAt(forecast.likely_out, tz);
  if (!likely) return null;
  const due = clockAt(forecast.inbound_arrival, tz);
  const city = forecast.inbound_from ? (cityFor(forecast.inbound_from) || forecast.inbound_from_city || forecast.inbound_from) : null;
  const from = city ? ` from ${city}` : '';
  const plane = forecast.inbound_arrived
    ? `Your plane only got in${from}${due ? ` at ${due}` : ''}`
    : `Your plane is still on its way${from}${due ? ` and isn’t due in until ${due}` : ''}`;
  // The same test as the email: the airline's time 15+ minutes past its schedule.
  const sched = Date.parse(status?.scheduled_out || '');
  const est = Date.parse(status?.estimated_out || '');
  const announced = Number.isFinite(sched) && Number.isFinite(est)
    ? (est - sched) / 60000 >= 15
    : Number(status?.departure_delay_minutes) >= 15;
  return {
    label: `Likely to leave around ${likely} or later`,
    text: `${plane}.${announced ? '' : ' The airline hasn’t announced a delay yet.'} Keep to the airline’s times in case it makes up time.`,
  };
}

/** Splits "UA 2210 · leaves 9:05 PM · Gate C17" into pieces that each stay on one line. */
export function nextLineParts(next) {
  return next ? String(next).split(' · ') : [];
}

const CONNECTION_LABELS = { missed: 'You may miss your connection', at_risk: 'Connection at risk', tight: 'Tight connection' };
const CONNECTION_TONES = { missed: 'bad', at_risk: 'warn', tight: 'warn' };

/**
 * A flight's connection to the next one (server/flight-foresight.js), in
 * the email's numbers: { tone, label, text, next } or null. `level` is only
 * set once it's news (shorter than booked), so an unchanged connection reads
 * as plain information.
 */
export function connectionLine(connection, cityFor = (c) => c) {
  if (!connection || !Number.isFinite(Number(connection.minutes))) return null;
  const minutes = Number(connection.minutes);
  const city = cityFor(connection.airport) || connection.airport;
  const leaves = clockAt(connection.departure_at, connection.timezone);
  const gate = [connection.next_terminal && `Terminal ${connection.next_terminal}`, connection.next_gate && `Gate ${connection.next_gate}`].filter(Boolean).join(' · ');
  return {
    tone: CONNECTION_TONES[connection.level] || 'info',
    label: CONNECTION_LABELS[connection.level] || 'Connection',
    text: minutes <= 0
      ? `Due into ${city} after your next flight is scheduled to leave`
      : `${formatDelay(minutes)} to connect in ${city}`,
    next: [connection.next_flight, leaves && `leaves ${leaves}`, gate].filter(Boolean).join(' · ') || null,
  };
}

/**
 * The one early warning a Trips card has room for, most urgent first: a
 * connection they may miss, the flight likely leaving late, then a tight or
 * at-risk connection. { tone, text } or null. Same words as the trip page.
 */
export function cardWarning(segment, cityFor = (c) => c) {
  if (!segment) return null;
  const level = segment.connection?.level || null;
  const connection = level ? connectionLine(segment.connection, cityFor) : null;
  // Short enough for a phone's card: "Connection at risk · 20 min in Chicago".
  const city = connection ? (cityFor(segment.connection.airport) || segment.connection.airport) : null;
  const asConnection = () => (level === 'missed'
    ? { tone: connection.tone, text: `You may miss your connection in ${city}` }
    : { tone: connection.tone, text: `${connection.label} · ${formatDelay(segment.connection.minutes)} in ${city}` });
  if (connection && level === 'missed') return asConnection();
  const forecast = forecastLine(segment.forecast, segment.status, cityFor);
  if (forecast) return { tone: 'warn', text: forecast.label };
  return connection ? asConnection() : null;
}

/**
 * The flight a Trips card should show: the next one not yet at its gate,
 * else the last one (just landed). Segments come from the batch endpoint,
 * already limited to those with live status.
 */
export function cardSegment(segments) {
  const live = (segments || []).filter((s) => s.status);
  if (!live.length) return null;
  return live.find((s) => s.status.state !== 'arrived' && s.status.state !== 'cancelled') || live[live.length - 1];
}

/**
 * Does a trip have a flight close enough to ask about live status? Checks
 * the booking's departure dates on the device's calendar, with a day of
 * slack either side for time zones and long trips.
 */
export function tripMightBeLive(trip, nowMs = Date.now()) {
  const stamps = [];
  for (const booking of trip || []) {
    const fd = booking?.flightData || booking?.flight_data || {};
    for (const slice of fd.slices || []) {
      for (const seg of slice?.segments || []) if (seg?.departing_at) stamps.push(seg.departing_at);
    }
  }
  return stamps.some((stamp) => {
    const m = String(stamp).match(/^(\d{4}-\d{2}-\d{2})/);
    if (!m) return false;
    const dayMs = Date.parse(`${m[1]}T12:00:00Z`);
    return dayMs - nowMs <= (LIVE_BEFORE_DEPARTURE_HOURS + 24) * 3600000 && nowMs - dayMs <= 2 * 24 * 3600000;
  });
}

/** A few common aircraft, by the ICAO code flight tracking reports. */
const AIRCRAFT = {
  A319: 'Airbus A319', A320: 'Airbus A320', A20N: 'Airbus A320neo', A321: 'Airbus A321', A21N: 'Airbus A321neo',
  A332: 'Airbus A330-200', A333: 'Airbus A330-300', A339: 'Airbus A330-900', A359: 'Airbus A350-900', A388: 'Airbus A380',
  BCS1: 'Airbus A220-100', BCS3: 'Airbus A220-300',
  B712: 'Boeing 717', B737: 'Boeing 737-700', B738: 'Boeing 737-800', B739: 'Boeing 737-900', B37M: 'Boeing 737 MAX 7',
  B38M: 'Boeing 737 MAX 8', B39M: 'Boeing 737 MAX 9', B752: 'Boeing 757-200', B753: 'Boeing 757-300', B763: 'Boeing 767-300',
  B764: 'Boeing 767-400', B772: 'Boeing 777-200', B77W: 'Boeing 777-300ER', B788: 'Boeing 787-8', B789: 'Boeing 787-9', B78X: 'Boeing 787-10',
  E170: 'Embraer 170', E175: 'Embraer 175', E75L: 'Embraer 175', E190: 'Embraer 190', E195: 'Embraer 195', E290: 'Embraer E190-E2',
  CRJ2: 'Bombardier CRJ200', CRJ7: 'Bombardier CRJ700', CRJ9: 'Bombardier CRJ900', DH8D: 'De Havilland Dash 8-400', AT76: 'ATR 72',
};

export function aircraftName(code) {
  if (!code) return null;
  return AIRCRAFT[String(code).toUpperCase()] || null;
}

/**
 * The track record's headline: "On time 6 of the last 10 days". A record
 * describes the flight number, not the customer's date.
 */
export function trackRecordHeadline(record) {
  if (!record) return null;
  return `On time ${record.on_time} of the last ${record.flights} ${record.flights === 1 ? 'flight' : 'flights'}`;
}

/** The supporting facts under the headline, as short phrases. */
export function trackRecordFacts(record) {
  if (!record) return [];
  const facts = [];
  const operated = record.flights - (record.cancelled || 0);
  if (operated > 0 && record.on_time / operated >= 0.8) facts.push('Usually arrives on time');
  else if (Number(record.median_arrival_delay_minutes) > 0) facts.push(`Typical delay ${formatDelay(record.median_arrival_delay_minutes)}`);
  if (record.late && Number(record.longest_arrival_delay_minutes) >= 30) {
    facts.push(`Longest delay ${formatDelay(record.longest_arrival_delay_minutes)}`);
  }
  if (record.cancelled) facts.push(`${record.cancelled} ${record.cancelled === 1 ? 'cancellation' : 'cancellations'}`);
  if (record.diverted) facts.push(`${record.diverted} ${record.diverted === 1 ? 'diversion' : 'diversions'}`);
  if (record.usual_terminal) facts.push(`Usually departs Terminal ${record.usual_terminal}`);
  const plane = aircraftName(record.usual_aircraft);
  if (plane) facts.push(`Usually ${/^[AEIOU]/i.test(plane) ? 'an' : 'a'} ${plane}`);
  return facts;
}

/** Tone for one day of the track record. */
export function dayTone(day) {
  if (!day) return 'muted';
  if (day.status === 'cancelled') return 'bad';
  if (day.status === 'diverted') return 'warn';
  const late = Number(day.arrival_delay_minutes) || 0;
  if (late > 45) return 'bad';
  if (late > 15) return 'warn';
  return 'good';
}
